import React, { Fragment, useState, useCallback, useMemo } from 'react';
import { Input, Button, Row, Col } from 'reactstrap';
import { CommonModal } from '../../../../../../../../GlobalComponents/Builders';
import { handleCopy } from '../../../../../../../../GlobalComponents/Helpers';
import { Copy, RotateCcw, ArrowDown, ArrowUp } from 'react-feather';

/**
 * TimestampConverterModal — Convert between Unix timestamps and human-readable dates.
 * Shows the result in UTC, EST, and a user-selectable timezone.
 * Also supports converting a readable date/time back to a Unix timestamp.
 */

const COMMON_TIMEZONES = [
    { label: 'UTC', value: 'UTC' },
    { label: 'US/Eastern (EST/EDT)', value: 'America/New_York' },
    { label: 'US/Central (CST/CDT)', value: 'America/Chicago' },
    { label: 'US/Mountain (MST/MDT)', value: 'America/Denver' },
    { label: 'US/Pacific (PST/PDT)', value: 'America/Los_Angeles' },
    { label: 'US/Alaska', value: 'America/Anchorage' },
    { label: 'US/Hawaii', value: 'Pacific/Honolulu' },
    { label: 'Canada/Atlantic', value: 'America/Halifax' },
    { label: 'Europe/London (GMT/BST)', value: 'Europe/London' },
    { label: 'Europe/Paris (CET/CEST)', value: 'Europe/Paris' },
    { label: 'Europe/Berlin', value: 'Europe/Berlin' },
    { label: 'Asia/Tokyo (JST)', value: 'Asia/Tokyo' },
    { label: 'Asia/Shanghai (CST)', value: 'Asia/Shanghai' },
    { label: 'Asia/Kolkata (IST)', value: 'Asia/Kolkata' },
    { label: 'Asia/Dubai (GST)', value: 'Asia/Dubai' },
    { label: 'Australia/Sydney (AEST)', value: 'Australia/Sydney' },
    { label: 'Pacific/Auckland (NZST)', value: 'Pacific/Auckland' },
    { label: 'America/Sao_Paulo (BRT)', value: 'America/Sao_Paulo' },
    { label: 'Africa/Johannesburg (SAST)', value: 'Africa/Johannesburg' },
];

const formatInTimezone = (date, timezone) => {
    try {
        return date.toLocaleString('en-US', {
            timeZone: timezone,
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
            timeZoneName: 'short',
        });
    } catch (e) {
        return 'Invalid timezone';
    }
};

const formatISO = (date, timezone) => {
    try {
        // Build an ISO-like string for the given timezone
        const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone: timezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        }).formatToParts(date);
        const get = (type) => (parts.find(p => p.type === type) || {}).value || '';
        return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}`;
    } catch (e) {
        return '';
    }
};

const TimestampConverterModal = ({ trigger }) => {

    const [modal, setModal] = useState(false);
    const [timestampInput, setTimestampInput] = useState('');
    const [dateInput, setDateInput] = useState('');
    const [selectedTimezone, setSelectedTimezone] = useState('America/New_York');
    const [mode, setMode] = useState('toDate'); // 'toDate' or 'toTimestamp'

    const openModal = () => setModal(true);

    const handleClear = () => {
        setTimestampInput('');
        setDateInput('');
    };

    const handleNow = () => {
        const now = Math.floor(Date.now() / 1000);
        setTimestampInput(String(now));
        setMode('toDate');
    };

    /**
     * Parse the timestamp input. Supports seconds and milliseconds.
     */
    const parsedDate = useMemo(() => {
        if (!timestampInput.trim()) return null;
        const num = Number(timestampInput.trim());
        if (isNaN(num)) return null;
        // If > 10 digits, assume milliseconds
        const ms = String(Math.floor(Math.abs(num))).length > 10 ? num : num * 1000;
        const d = new Date(ms);
        if (isNaN(d.getTime())) return null;
        return d;
    }, [timestampInput]);

    /**
     * Parse a date string input back to a timestamp.
     */
    const parsedTimestamp = useMemo(() => {
        if (!dateInput.trim()) return null;
        const d = new Date(dateInput.trim());
        if (isNaN(d.getTime())) return null;
        return Math.floor(d.getTime() / 1000);
    }, [dateInput]);

    const renderTimestampToDate = () => {
        if (!timestampInput.trim()) {
            return <p style={{ color: '#999', textAlign: 'center', padding: 20 }}>Enter a Unix timestamp above to see it converted</p>;
        }
        if (!parsedDate) {
            return <p style={{ color: '#dc3545', textAlign: 'center', padding: 20 }}>Invalid timestamp</p>;
        }

        const rows = [
            { label: 'UTC', value: formatInTimezone(parsedDate, 'UTC') },
            { label: 'EST/EDT', value: formatInTimezone(parsedDate, 'America/New_York') },
            { label: COMMON_TIMEZONES.find(t => t.value === selectedTimezone)?.label || selectedTimezone, value: formatInTimezone(parsedDate, selectedTimezone) },
        ];

        // Deduplicate if selected timezone is already UTC or EST
        const seen = new Set();
        const uniqueRows = rows.filter(r => {
            if (seen.has(r.label)) return false;
            seen.add(r.label);
            return true;
        });

        return (
            <div>
                <table style={{ width: '100%', fontSize: 13 }}>
                    <tbody>
                        {uniqueRows.map((row, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid rgba(128,128,128,0.1)' }}>
                                <td style={{ padding: '8px 12px', fontWeight: 600, width: 180 }}>{row.label}</td>
                                <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{row.value}</td>
                                <td style={{ padding: '8px 4px', width: 30 }}>
                                    <Copy size={12} style={{ cursor: 'pointer', opacity: 0.4 }} onClick={() => handleCopy(row.value)} />
                                </td>
                            </tr>
                        ))}
                        <tr style={{ borderBottom: '1px solid rgba(128,128,128,0.1)' }}>
                            <td style={{ padding: '8px 12px', fontWeight: 600 }}>ISO 8601 (UTC)</td>
                            <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{parsedDate.toISOString()}</td>
                            <td style={{ padding: '8px 4px', width: 30 }}>
                                <Copy size={12} style={{ cursor: 'pointer', opacity: 0.4 }} onClick={() => handleCopy(parsedDate.toISOString())} />
                            </td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid rgba(128,128,128,0.1)' }}>
                            <td style={{ padding: '8px 12px', fontWeight: 600 }}>Relative</td>
                            <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{getRelativeTime(parsedDate)}</td>
                            <td style={{ padding: '8px 4px', width: 30 }}></td>
                        </tr>
                        <tr>
                            <td style={{ padding: '8px 12px', fontWeight: 600 }}>Milliseconds</td>
                            <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{parsedDate.getTime()}</td>
                            <td style={{ padding: '8px 4px', width: 30 }}>
                                <Copy size={12} style={{ cursor: 'pointer', opacity: 0.4 }} onClick={() => handleCopy(String(parsedDate.getTime()))} />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    const renderDateToTimestamp = () => {
        if (!dateInput.trim()) {
            return <p style={{ color: '#999', textAlign: 'center', padding: 20 }}>Enter a date/time string above to get the Unix timestamp</p>;
        }
        if (parsedTimestamp === null) {
            return <p style={{ color: '#dc3545', textAlign: 'center', padding: 20 }}>Invalid date format. Try: 2024-01-15 14:30:00, Jan 15 2024, or ISO 8601 format</p>;
        }

        return (
            <div>
                <table style={{ width: '100%', fontSize: 13 }}>
                    <tbody>
                        <tr style={{ borderBottom: '1px solid rgba(128,128,128,0.1)' }}>
                            <td style={{ padding: '8px 12px', fontWeight: 600, width: 180 }}>Unix (seconds)</td>
                            <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{parsedTimestamp}</td>
                            <td style={{ padding: '8px 4px', width: 30 }}>
                                <Copy size={12} style={{ cursor: 'pointer', opacity: 0.4 }} onClick={() => handleCopy(String(parsedTimestamp))} />
                            </td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid rgba(128,128,128,0.1)' }}>
                            <td style={{ padding: '8px 12px', fontWeight: 600 }}>Unix (milliseconds)</td>
                            <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{parsedTimestamp * 1000}</td>
                            <td style={{ padding: '8px 4px', width: 30 }}>
                                <Copy size={12} style={{ cursor: 'pointer', opacity: 0.4 }} onClick={() => handleCopy(String(parsedTimestamp * 1000))} />
                            </td>
                        </tr>
                        <tr>
                            <td style={{ padding: '8px 12px', fontWeight: 600 }}>ISO 8601</td>
                            <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{new Date(parsedTimestamp * 1000).toISOString()}</td>
                            <td style={{ padding: '8px 4px', width: 30 }}>
                                <Copy size={12} style={{ cursor: 'pointer', opacity: 0.4 }} onClick={() => handleCopy(new Date(parsedTimestamp * 1000).toISOString())} />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    const modalBody = (
        <div>
            <p style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
                Convert between Unix timestamps and human-readable dates. Supports seconds and milliseconds.
            </p>

            {/* Mode toggle */}
            <div className='d-flex align-items-center gap-2' style={{ marginBottom: 16 }}>
                <Button size='sm' color={mode === 'toDate' ? 'dark' : 'light'}
                    style={{ fontSize: 11, padding: '4px 12px' }}
                    onClick={() => setMode('toDate')}>
                    <ArrowDown size={11} className='me-1' /> Timestamp → Date
                </Button>
                <Button size='sm' color={mode === 'toTimestamp' ? 'dark' : 'light'}
                    style={{ fontSize: 11, padding: '4px 12px' }}
                    onClick={() => setMode('toTimestamp')}>
                    <ArrowUp size={11} className='me-1' /> Date → Timestamp
                </Button>
                <div className='ms-auto d-flex gap-1'>
                    <Button size='sm' color='light' onClick={handleNow} title='Use current time' style={{ fontSize: 11 }}>
                        Now
                    </Button>
                    <Button size='sm' color='light' onClick={handleClear} title='Clear'>
                        <RotateCcw size={12} />
                    </Button>
                </div>
            </div>

            {mode === 'toDate' && (
                <>
                    {/* Timestamp input */}
                    <Row className='align-items-end' style={{ marginBottom: 12 }}>
                        <Col md="8">
                            <span style={{ fontSize: 11, fontWeight: 600 }}>Unix Timestamp</span>
                            <Input
                                type='text'
                                value={timestampInput}
                                onChange={(e) => setTimestampInput(e.target.value)}
                                placeholder='e.g. 1700000000 or 1700000000000'
                                style={{ fontFamily: 'monospace', fontSize: 12, marginTop: 4 }}
                            />
                        </Col>
                        <Col md="4">
                            <span style={{ fontSize: 11, fontWeight: 600 }}>Extra Timezone</span>
                            <Input
                                type='select'
                                value={selectedTimezone}
                                onChange={(e) => setSelectedTimezone(e.target.value)}
                                style={{ fontSize: 12, marginTop: 4 }}
                            >
                                {COMMON_TIMEZONES.map(tz => (
                                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                                ))}
                            </Input>
                        </Col>
                    </Row>

                    {/* Results */}
                    <div style={{
                        border: '1px solid rgba(128,128,128,0.2)',
                        borderRadius: 4,
                        overflow: 'hidden',
                        background: 'rgba(128,128,128,0.02)',
                    }}>
                        {renderTimestampToDate()}
                    </div>
                </>
            )}

            {mode === 'toTimestamp' && (
                <>
                    {/* Date input */}
                    <div style={{ marginBottom: 12 }}>
                        <span style={{ fontSize: 11, fontWeight: 600 }}>Date / Time String</span>
                        <Input
                            type='text'
                            value={dateInput}
                            onChange={(e) => setDateInput(e.target.value)}
                            placeholder='e.g. 2024-01-15 14:30:00, Jan 15 2024 2:30 PM, or ISO format'
                            style={{ fontFamily: 'monospace', fontSize: 12, marginTop: 4 }}
                        />
                        <small style={{ color: '#999', fontSize: 10 }}>Accepts most common formats: ISO 8601, RFC 2822, or natural date strings</small>
                    </div>

                    {/* Results */}
                    <div style={{
                        border: '1px solid rgba(128,128,128,0.2)',
                        borderRadius: 4,
                        overflow: 'hidden',
                        background: 'rgba(128,128,128,0.02)',
                    }}>
                        {renderDateToTimestamp()}
                    </div>
                </>
            )}
        </div>
    );

    if (trigger) {
        return (
            <Fragment>
                <span onClick={openModal} style={{ display: 'inline-flex' }}>{trigger}</span>
                <CommonModal isOpen={modal} title='Timestamp Converter' toggler={() => setModal(false)} size='lg' onSubmit={false} onCancelLabel='Close'>
                    {modalBody}
                </CommonModal>
            </Fragment>
        );
    }

    return (
        <CommonModal isOpen={modal} title='Timestamp Converter' toggler={() => setModal(false)} size='lg' onSubmit={false} onCancelLabel='Close'>
            {modalBody}
        </CommonModal>
    );
};

/**
 * Simple relative time helper (no moment dependency needed).
 */
function getRelativeTime(date) {
    const now = new Date();
    const diffMs = now - date;
    const absDiff = Math.abs(diffMs);
    const isFuture = diffMs < 0;

    const seconds = Math.floor(absDiff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    let text;
    if (seconds < 60) text = `${seconds} second${seconds !== 1 ? 's' : ''}`;
    else if (minutes < 60) text = `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    else if (hours < 24) text = `${hours} hour${hours !== 1 ? 's' : ''}`;
    else if (days < 30) text = `${days} day${days !== 1 ? 's' : ''}`;
    else if (months < 12) text = `${months} month${months !== 1 ? 's' : ''}`;
    else text = `${years} year${years !== 1 ? 's' : ''}`;

    return isFuture ? `in ${text}` : `${text} ago`;
}

export default TimestampConverterModal;
